/**
 * Takes a number and returns a compact-formatted version of it (e.g. `1.5K`)
 *
 * @param num The number to format
 * @returns string with the formatted number
 */
export function formatNumberCompact(num: number) {
  const formatter = Intl.NumberFormat('en', { notation: 'compact' });
  return formatter.format(num);
}

/**
 * Locale-aware *integer* parser. There is no built-in `Intl.parseNumber` yet
 * (TC39 proposal-intl-numberformat-v3 is still pending), so we discover the
 * locale's grouping + decimal separators via `formatToParts`, strip the
 * grouping characters, and **truncate at the decimal character** so
 * fractional input collapses to its integer portion instead of concatenating
 * the digits across the decimal point.
 *
 * Why truncate instead of just `parseFloat`? Every caller of this helper is
 * an integer-only domain (seat counts, quantities, page sizes). Returning
 * 1234 for "1,234.56" matches what the backend's `.to_i` does — without the
 * silent-corruption hazard of a naive `/\D/g` strip, which would produce
 * 123456 for the same input.
 *
 * Examples for `en-US`:  "1,234" → 1234,  "1,234.56" → 1234,  "" → null
 * Examples for `fr-FR`:  "1 234" → 1234,  "1 234,56" → 1234,  "abc" → null
 *                        (the space above is U+202F, the FR grouping char)
 */
export function parseLocaleInteger(raw: string, locale: string): number | null {
  if (raw === '') return null;

  // Discover separators by formatting a known value with this locale.
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  const groupSep = parts.find((p) => p.type === 'group')?.value ?? '';
  const decimalSep = parts.find((p) => p.type === 'decimal')?.value ?? '.';

  let cleaned = groupSep ? raw.split(groupSep).join('') : raw;
  const decimalIdx = cleaned.indexOf(decimalSep);
  if (decimalIdx !== -1) cleaned = cleaned.slice(0, decimalIdx);

  // Anything other than digits left is invalid — return null rather than
  // silently coerce. ("1a2" → null, not 12.)
  if (!/^\d+$/.test(cleaned)) return null;

  return Number.parseInt(cleaned, 10);
}
